import { NonEmptyString } from "@heliopolis/schema/non-empty-string.js";
import { date, enum as zodEnum, looseObject, object, string, url } from "zod";
/** @import { output } from "zod" */

export const YouTubeVideoURL = url().pipe(
  string().refine((videoURL) => {
    const parsedURL = new URL(videoURL);

    if (
      parsedURL.protocol !== "https:" ||
      parsedURL.username ||
      parsedURL.password ||
      parsedURL.port ||
      parsedURL.searchParams.has("list")
    )
      return false;

    if (parsedURL.hostname === "youtu.be") return /^\/[A-Za-z0-9_-]{11}$/.test(parsedURL.pathname);

    if (!["youtube.com", "www.youtube.com", "m.youtube.com"].includes(parsedURL.hostname))
      return false;

    if (parsedURL.pathname === "/watch") {
      const identifiers = parsedURL.searchParams.getAll("v");
      return identifiers.length === 1 && /^[A-Za-z0-9_-]{11}$/.test(identifiers[0]);
    }

    return /^\/shorts\/[A-Za-z0-9_-]{11}$/.test(parsedURL.pathname);
  }, "Expected a supported HTTPS YouTube single-video URL"),
);
/** @typedef {output<typeof YouTubeVideoURL>} YouTubeVideoURL */

/**
 * A trimmed YouTube URL input normalized to a canonical watch URL.
 * @example
 * YouTubeVideoURLInput.decode(" https://youtu.be/dQw4w9WgXcQ ");
 * // "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
 */
export const YouTubeVideoURLInput = string()
  .trim()
  .pipe(YouTubeVideoURL)
  .transform((videoURL) => {
    const parsedURL = new URL(videoURL);
    const videoId =
      parsedURL.pathname === "/watch"
        ? parsedURL.searchParams.get("v")
        : parsedURL.pathname.split("/").at(-1);

    return `https://www.youtube.com/watch?v=${videoId}`;
  });
/** @typedef {output<typeof YouTubeVideoURLInput>} YouTubeVideoURLInput */

export const YouTubeMediaDownloadFormat = zodEnum({
  AudioOnly: "audio-only",
  AudioVideo: "audio+video",
});
/**
 * @typedef {output<typeof YouTubeMediaDownloadFormat>}
 * YouTubeMediaDownloadFormat
 */

export const YouTubeDlMetadata = looseObject({
  /**
   * The YouTube video identifier.
   * @example "ieWcNNlzY4g"
   */
  id: NonEmptyString,
  /** The video's title as supplied by yt-dlp. */
  title: NonEmptyString,
  /**
   * yt-dlp's human-readable description of the selected format.
   * @example "394 - 144x144 (144p)+140 - audio only (medium)"
   */
  format: NonEmptyString,
  /**
   * yt-dlp identifier(s) for the selected format.
   * @example "394+140"
   */
  format_id: NonEmptyString,
  /**
   * File extension of the downloaded media.
   * @example "mp4"
   */
  ext: NonEmptyString,
});
/**
 * @typedef {output<typeof YouTubeDlMetadata>}
 * YouTubeDlMetadata
 */

export const DownloadedYouTubeMedia = object({
  /**
   * Absolute path to the downloaded media file.
   * @example "/media/ieWcNNlzY4g.mp4"
   */
  filePath: NonEmptyString,
  format: object({
    /**
     * Human-readable description of the selected format.
     * @example "394 - 144x144 (144p)+140 - audio only (medium)"
     */
    description: NonEmptyString,
    /**
     * File extension of the downloaded media.
     * @example "mp4"
     */
    extension: NonEmptyString,
    /**
     * yt-dlp identifier(s) for the selected format.
     * @example "394+140"
     */
    id: NonEmptyString,
  }),
  /**
   * Time immediately before yt-dlp was invoked.
   * @example new Date("2026-10-06T18:05:31.803Z")
   */
  fetchStartedAt: date(),
  /**
   * Time when yt-dlp completed the download.
   * @example new Date("2026-10-06T18:05:35.001Z")
   */
  fetchEndedAt: date(),
  /**
   * Time when the downloaded file was confirmed to exist and be non-empty.
   * @example new Date("2026-10-06T18:05:35.006Z")
   */
  fileConfirmedAt: date(),
  /** Validated metadata returned by yt-dlp for the downloaded video. */
  metadata: YouTubeDlMetadata,
});
/**
 * @typedef {output<typeof DownloadedYouTubeMedia>}
 * DownloadedYouTubeMedia
 */
